import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey,
  getApiV1IndividualpaymentParcelsParcelidSerialsQueryKey,
  getApiV1PecegepayBilletCoursefinpersonpaymentidPdfQueryOptions,
  useGetApiV1PecegepayBilletCoursefinpersonpaymentidOrder,
  usePostApiV1PecegepayBilletCoursefinpersonpaymentid,
} from '@/shared/api/generated';

import type { BilletIssueRoute } from '../utils/billetIssueRoute';
import { readBilletRegistration } from '../utils/billetIssueRoute';
import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/**
 * `EFinancialOrderLogType.BilletIndividualPaymnent` — origem do pedido no log
 * financeiro do PecegePay. O nome com o typo é do próprio spec.
 */
const BILLET_SOURCE_INDIVIDUAL_PAYMENT = 7;

/** Intervalo entre leituras do pedido, em ms. */
const POLL_INTERVAL_MS = 3000;

/**
 * Teto de leituras do pedido antes de desistir.
 *
 * Com o intervalo de 3s dá cerca de um minuto. Não é o tempo que o provedor leva
 * — é o tempo que o operador aguenta olhar um diálogo girando. Estourar o teto
 * **não** cancela o registro no provedor: só para de esperar, e a mensagem diz
 * isso, para o operador não registrar de novo achando que falhou.
 */
const MAX_POLL_ATTEMPTS = 20;

/** Em que ponto do fluxo o pedido de boleto está. */
export type BilletPhase =
  | 'idle'
  | 'downloading'
  | 'registering'
  | 'polling'
  | 'ready'
  | 'timedOut'
  | 'failed';

/**
 * Emissão do boleto de uma parcela, nos dois caminhos do legado.
 *
 * Qual caminho seguir é decisão de `resolveBilletIssueRoute`; este hook executa:
 *
 * - **`download`** — `GET /pecegepay/billet/{id}/pdf`, que devolve `Blob`. O PDF
 *   é aberto por object URL em vez de link direto porque a rota exige o header de
 *   autenticação: um `window.open` na URL da API cairia em 401.
 * - **`register`** — `POST /pecegepay/billet/{id}` e depois leituras de
 *   `GET /pecegepay/billet/{id}/order` até o provedor carimbar `registeredAt` ou
 *   `error` no boleto (ver `readBilletRegistration`).
 *
 * O polling usa o `refetchInterval` do TanStack Query na forma de função, que se
 * desliga sozinho no estado terminal — sem `useEffect` sincronizando estado com
 * dado, que é onde esse tipo de fluxo costuma apodrecer.
 *
 * Invalida as parcelas do contrato e os boletos da parcela quando o registro
 * conclui: `hasBilletGenerated` e a lista de boletos mudam.
 *
 * @param financialPersonId - Contrato da rota; `null` só pula a invalidação.
 * @param parcelId - Parcela alvo; `null` torna as ações inertes.
 * @returns Fase corrente, o que fazer e o link do PDF quando existe.
 *
 * @example
 * ```tsx
 * const billet = useParcelBillet(101, 42);
 * billet.start('register');
 * ```
 */
export function useParcelBillet(financialPersonId: number | null, parcelId: number | null) {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<BilletPhase>('idle');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [failure, setFailure] = useState<PlanWriteError | { readonly message: string } | null>(
    null,
  );
  const [attempts, setAttempts] = useState(0);

  const register = usePostApiV1PecegepayBilletCoursefinpersonpaymentid();

  const invalidate = useCallback(() => {
    if (parcelId !== null) {
      void queryClient.invalidateQueries({
        queryKey: getApiV1IndividualpaymentParcelsParcelidSerialsQueryKey(parcelId),
      });
    }
    if (financialPersonId !== null) {
      void queryClient.invalidateQueries({
        queryKey:
          getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey(
            financialPersonId,
          ),
      });
    }
  }, [financialPersonId, parcelId, queryClient]);

  // O pedido só é lido enquanto se espera o provedor. `refetchInterval` como
  // função é o que desliga o polling no estado terminal sem efeito nenhum — e é
  // também onde a tentativa é contada, porque é o ponto por onde cada leitura
  // passa. O callback roda no núcleo do Query, depois do fetch, não durante o
  // render.
  const order = useGetApiV1PecegepayBilletCoursefinpersonpaymentidOrder(parcelId ?? 0, {
    query: {
      enabled: phase === 'polling' && parcelId !== null,
      refetchInterval: query => {
        const outcome = readBilletRegistration(query.state.data);
        if (outcome.status !== 'pending') return false;

        setAttempts(current => current + 1);
        return query.state.dataUpdateCount >= MAX_POLL_ATTEMPTS ? false : POLL_INTERVAL_MS;
      },
    },
  });

  const outcome = readBilletRegistration(order.data);

  // Fase derivada, não guardada: enquanto o polling roda, quem manda é o dado do
  // pedido. Guardar `ready`/`failed` em estado exigiria um efeito para escrevê-lo,
  // e o efeito é justamente o que este desenho evita.
  const resolvedPhase: BilletPhase =
    phase !== 'polling'
      ? phase
      : outcome.status === 'registered'
        ? 'ready'
        : outcome.status === 'failed'
          ? 'failed'
          : attempts >= MAX_POLL_ATTEMPTS
            ? 'timedOut'
            : 'polling';

  const resolvedPdfUrl = outcome.status === 'registered' ? outcome.pdfUrl : pdfUrl;
  const resolvedFailure = outcome.status === 'failed' ? { message: outcome.message } : failure;

  const downloadPdf = useCallback(async () => {
    if (parcelId === null) return;

    setPhase('downloading');
    setFailure(null);

    try {
      // `fetchQuery` em vez de um `useQuery` habilitado sob demanda: o download é
      // um evento, não um estado da tela, e o cliente gerado já carrega o header
      // de autenticação.
      const blob = await queryClient.fetchQuery(
        getApiV1PecegepayBilletCoursefinpersonpaymentidPdfQueryOptions(parcelId),
      );

      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPhase('ready');
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      setFailure(classifyPlanWriteError(error));
      setPhase('failed');
    }
  }, [parcelId, queryClient]);

  const start = useCallback(
    (route: BilletIssueRoute) => {
      if (parcelId === null) return;

      setPdfUrl(null);
      setFailure(null);
      setAttempts(0);

      if (route === 'download') {
        void downloadPdf();
        return;
      }

      setPhase('registering');
      register.mutate(
        {
          courseFinPersonPaymentId: parcelId,
          params: { source: BILLET_SOURCE_INDIVIDUAL_PAYMENT },
        },
        {
          onSuccess: data => {
            invalidate();

            // O provedor às vezes já devolve o boleto pronto no POST. Nesse caso
            // não há o que esperar, e abrir o polling só para descobrir isso
            // custaria uma request e três segundos.
            const immediateUrl = data.pdfFileUrl ?? data.billetUrl ?? null;
            if (immediateUrl !== null) {
              setPdfUrl(immediateUrl);
              setPhase('ready');
              return;
            }

            setPhase('polling');
          },
          onError: error => {
            setFailure(classifyPlanWriteError(error));
            setPhase('failed');
          },
        },
      );
    },
    [parcelId, downloadPdf, register, invalidate],
  );

  const reset = useCallback(() => {
    setPhase('idle');
    setPdfUrl(null);
    setFailure(null);
    setAttempts(0);
    register.reset();
  }, [register]);

  return {
    phase: resolvedPhase,
    pdfUrl: resolvedPdfUrl,
    failure: resolvedFailure,
    /** Leituras do pedido já feitas, para a linha de progresso do diálogo. */
    attempts,
    maxAttempts: MAX_POLL_ATTEMPTS,
    start,
    reset,
  } as const;
}
